import { useEffect, useMemo, useState } from "react";
import Drawer from "./Drawer";
import Icon from "./Icon";
import { StatusBadge, ClientPendingBadge } from "./Badges";
import PaymentExpensesSection from "./PaymentExpensesSection";
import {
  getEmployeeTask,
  updateEmployeeTask,
  updateEmployeeTaskStatus,
  verifyEmployeeTask,
  assignEmployeeTask,
  unassignEmployeeTask,
  setEmployeeTaskReviewer,
  listAdminTaskNotes,
  createAdminTaskNote,
} from "../api/employeeTaskService";
import { useAuth } from "../contexts/AuthContext";
import { listEmployees } from "../api/employeeService";
import { paginatedRows } from "../utils/pagination";
import {
  myTask,
  updateMyTaskStatus,
  setMyTaskClientPending,
  listMyTaskNotes,
  createMyTaskNote,
  myTaskTransferTargets,
  transferMyTask,
} from "../api/myTaskService";
import SearchableSelect from "./SearchableSelect";
import { formatDate, formatDateTime, daysFromToday } from "../utils/formatters";
import { showApiError, showToast } from "../utils/toast";

const PRIORITY_LABEL = { low: "Low", medium: "Medium", high: "High" };
const PRIORITY_CLS = { low: "badge-neutral", medium: "badge-warning", high: "badge-danger" };

export default function TaskDrawer({ taskId, role, onClose, onChanged }) {
  const [task, setTask] = useState(null);
  const [notes, setNotes] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Shared by the assignee and reviewer pickers.
  const staffOptions = useMemo(
    () => employees.map((e) => ({ id: e.id, label: e.name, hint: e.department?.name })),
    [employees],
  );
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTargets, setTransferTargets] = useState([]);
  const [transferTargetId, setTransferTargetId] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);

  const isAdmin = role === "admin";
  const { user: authUser, hasPermission } = useAuth();

  useEffect(() => {
    if (!taskId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [taskRes, notesRes, empRes] = await Promise.all([
          isAdmin ? getEmployeeTask(taskId) : myTask(taskId),
          isAdmin ? listAdminTaskNotes(taskId) : listMyTaskNotes(taskId),
          isAdmin ? listEmployees({ per_page: 100 }) : Promise.resolve(null),
        ]);
        if (!alive) return;
        setTask(taskRes.data);
        const noteRows = notesRes.data?.data || notesRes.data || [];
        setNotes(Array.isArray(noteRows) ? noteRows : []);
        if (empRes) setEmployees(paginatedRows(empRes));
      } catch (err) {
        showApiError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [taskId, isAdmin]);

  if (!taskId) return null;

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      const res = isAdmin
        ? await updateEmployeeTaskStatus(taskId, { status })
        : await updateMyTaskStatus(taskId, { status });
      setTask((t) => ({ ...t, ...res.data }));
      showToast("success", "Status updated");
      onChanged?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    setSaving(true);
    try {
      const res = await verifyEmployeeTask(taskId);
      setTask((t) => ({ ...t, ...res.data }));
      showToast("success", "Task verified");
      onChanged?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  // "Waiting on client" overlay flag — admin sets it via the generic task
  // update, the assignee via the dedicated my-task endpoint.
  const setClientPending = async (value) => {
    setSaving(true);
    try {
      const res = isAdmin
        ? await updateEmployeeTask(taskId, { is_client_pending: value })
        : await setMyTaskClientPending(taskId, value);
      setTask((t) => ({ ...t, ...res.data }));
      showToast("success", value ? "Marked as waiting on client" : "Client-pending cleared");
      onChanged?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  // Admin-only: edit the deadline and/or the reminder date. Backend clamps
  // reminder_date if the new due_date is earlier than the existing reminder.
  const updateDates = async (changes) => {
    setSaving(true);
    try {
      const res = await updateEmployeeTask(taskId, changes);
      setTask((t) => ({ ...t, ...res.data }));
      showToast("success", "Updated");
      onChanged?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const changeAssignee = async (employeeId) => {
    setSaving(true);
    try {
      if (!employeeId) {
        await unassignEmployeeTask(taskId);
        showToast("success", "Task unassigned");
      } else {
        const res = await assignEmployeeTask(taskId, Number(employeeId));
        setTask((t) => ({ ...t, ...res.data }));
        showToast("success", "Task assigned");
      }
      // Reload to pick up the freshly loaded employee relation.
      const refreshed = await getEmployeeTask(taskId);
      setTask(refreshed.data);
      onChanged?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const changeReviewer = async (reviewerId) => {
    setSaving(true);
    try {
      // Passing null clears the reviewer; a numeric id sets it. Backend
      // notifies the reviewer when it actually changes.
      await setEmployeeTaskReviewer(taskId, reviewerId ? Number(reviewerId) : null);
      const refreshed = await getEmployeeTask(taskId);
      setTask(refreshed.data);
      showToast("success", reviewerId ? "Reviewer set" : "Reviewer cleared");
      onChanged?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const openTransfer = async () => {
    setTransferOpen(true);
    if (transferTargets.length > 0) return;
    setTransferLoading(true);
    try {
      const res = await myTaskTransferTargets();
      const rows = res.data?.data || res.data || [];
      setTransferTargets(Array.isArray(rows) ? rows : []);
    } catch (err) {
      showApiError(err);
    } finally {
      setTransferLoading(false);
    }
  };

  const submitTransfer = async () => {
    if (!transferTargetId) return;
    setSaving(true);
    try {
      await transferMyTask(taskId, { to_employee_id: Number(transferTargetId) });
      showToast("success", "Task transferred");
      onChanged?.();
      onClose?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  // Re-fetch the task after a linked-payment change (expense add/edit/remove)
  // so the breakdown + total refresh, and let the parent list refresh too.
  const reloadTask = async () => {
    try {
      const res = isAdmin ? await getEmployeeTask(taskId) : await myTask(taskId);
      setTask(res.data);
      onChanged?.();
    } catch (err) {
      showApiError(err);
    }
  };

  const addNote = async () => {
    if (!reply.trim()) return;
    setSaving(true);
    try {
      const res = isAdmin
        ? await createAdminTaskNote(taskId, { note: reply })
        : await createMyTaskNote(taskId, { note: reply });
      setNotes((n) => [res.data, ...n]);
      setReply("");
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const tm = task?.task_master;
  const dueIn = task?.due_date ? daysFromToday(task.due_date) : null;

  // Role flags for the employee footer. The backend ships `employee` and
  // `reviewer` relations with `user_id`, so we can match by the auth user's
  // id without needing to thread the employee record through context.
  const myUserId = authUser?.id ? Number(authUser.id) : null;
  const isAssignee = !!(task && myUserId && Number(task.employee?.user_id) === myUserId);
  const hasReviewer = !!task?.reviewer_id;
  const isReviewer = !!(task && hasReviewer && myUserId && Number(task.reviewer?.user_id) === myUserId);
  // Only the current assignee drives the workflow. When a reviewer is set,
  // completion is theirs alone — so the assignee must transfer the task to the
  // reviewer (making them the assignee) before it can be completed.
  const canMarkInProgress = isAssignee;
  const canMarkComplete = isAssignee && (!hasReviewer || isReviewer);
  // Admin always; the assignee can flag their own task (unless verified-locked).
  const canEditClientPending = isAdmin || (isAssignee && !task?.is_verified);

  // Linked bill for a one-off task (0 or 1). Expenses can be managed by an
  // admin, anyone with payments.edit, or the task's assignee.
  const paymentInstance = Array.isArray(task?.payment_instances) ? task.payment_instances[0] : null;
  const canManageExpenses = isAdmin || (hasPermission?.("payments.edit") ?? false) || isAssignee;

  const footer = task && (
    <>
      {isAdmin ? (
        <>
          {!task.is_verified && task.status === "completed" && (
            <button className="btn btn-primary" onClick={verify} disabled={saving}>
              <Icon name="check" size={13} /> Verify
            </button>
          )}
        </>
      ) : task.is_verified ? (
        <span className="t-muted text-xs" style={{ alignSelf: "center" }}>
          <Icon name="check" size={11} /> Verified — locked. Contact admin for changes.
        </span>
      ) : (
        <>
          {isAssignee && (task.status === "pending" || task.status === "in_progress") && (
            <button
              className="btn btn-ghost"
              onClick={openTransfer}
              disabled={saving}
              title="Transfer this task to another employee"
            >
              <Icon name="user" size={13} /> Transfer
            </button>
          )}
          {isAssignee && hasReviewer && !isReviewer && (
            <span className="t-muted text-xs" style={{ alignSelf: "center" }}>
              When the work's done, transfer this to the reviewer ({task.reviewer?.name || "—"}) to complete it.
            </span>
          )}
          {!isAssignee && (
            <span className="t-muted text-xs" style={{ alignSelf: "center" }}>
              Only the current assignee can update this task.
            </span>
          )}
          {canMarkInProgress && (
            <button
              className="btn btn-secondary"
              onClick={() => updateStatus("in_progress")}
              disabled={saving || task.status === "in_progress"}
            >
              Mark in progress
            </button>
          )}
          {canMarkComplete && (
            <button
              className="btn btn-primary"
              onClick={() => updateStatus("completed")}
              disabled={saving || task.status === "completed"}
            >
              <Icon name="check" size={13} /> Mark complete
            </button>
          )}
        </>
      )}
    </>
  );

  return (
    <Drawer
      open={!!taskId}
      onClose={onClose}
      title={tm?.name || "Task"}
      subtitle={task?.business?.business_name}
      wide
      footer={footer}
    >
      {loading || !task ? (
        <div className="center-screen" style={{ height: 200 }}>
          <div className="spinner" />
        </div>
      ) : (
        <div className="col gap-md">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>Status</div>
              {isAdmin ? (
                <select
                  className="select"
                  style={{ marginTop: 6 }}
                  value={task.status}
                  disabled={saving}
                  onChange={(e) => updateStatus(e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              ) : (
                <div style={{ marginTop: 6 }}><StatusBadge status={task.status} /></div>
              )}
              <div style={{ marginTop: 8 }}>
                {canEditClientPending ? (
                  <label
                    style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-700)", cursor: saving ? "default" : "pointer" }}
                    title="Waiting on inputs from the client"
                  >
                    <input
                      type="checkbox"
                      checked={!!task.is_client_pending}
                      disabled={saving}
                      onChange={(e) => setClientPending(e.target.checked)}
                    />
                    Client pending
                  </label>
                ) : (
                  task.is_client_pending && <ClientPendingBadge />
                )}
              </div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>Due</div>
              {isAdmin ? (
                <input
                  type="date"
                  className="input"
                  style={{ marginTop: 4, fontWeight: 600 }}
                  value={(task.due_date || "").slice(0, 10)}
                  disabled={saving}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    updateDates({ due_date: e.target.value });
                  }}
                />
              ) : (
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: dueIn != null && dueIn < 0 ? "var(--danger)" : "var(--ink-900)",
                    marginTop: 4,
                  }}
                >
                  {formatDate(task.due_date)}
                </div>
              )}
              <div className="text-xs t-muted">
                {dueIn === 0 ? "Today" : dueIn != null && dueIn < 0 ? `${Math.abs(dueIn)} days overdue` : `in ${dueIn} days`}
              </div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>Verified</div>
              <div style={{ marginTop: 6 }}>
                {task.is_verified ? (
                  <span className="badge badge-success">Verified</span>
                ) : (
                  <span className="badge badge-neutral">Pending review</span>
                )}
              </div>
            </div>
          </div>

          {/* Auto-invoicing schedule. Armed at verification, so this only ever
              shows on a verified task. The amount is deliberately not here —
              it's hidden from task payloads so assignees can't read the fee. */}
          {task.auto_invoice && task.invoice_due_on && (
            <div className="card" style={{ padding: 12 }}>
              <div className="row gap-sm" style={{ alignItems: "center" }}>
                <Icon name="file-text" size={13} />
                <span className="text-sm">
                  Invoice scheduled for <strong>{formatDate(task.invoice_due_on)}</strong>
                </span>
              </div>
            </div>
          )}

          {!isAdmin && transferOpen && !task.is_verified && (task.status === "pending" || task.status === "in_progress") && (
            <div className="card" style={{ borderColor: "var(--primary-300)" }}>
              <div className="card-head">
                <div className="card-title">Transfer this task</div>
                <button
                  className="icon-btn"
                  title="Cancel"
                  onClick={() => { setTransferOpen(false); setTransferTargetId(""); }}
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
              <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div className="text-xs t-muted">
                  Pick a colleague to hand this task to. They'll be notified and the change is logged.
                </div>
                {transferLoading ? (
                  <div className="text-xs t-muted">Loading employees…</div>
                ) : (
                  <SearchableSelect
                    value={transferTargetId}
                    onChange={(v) => setTransferTargetId(v)}
                    options={transferTargets.map((e) => ({
                      id: e.id,
                      label: e.name + (e.employee_id ? ` · ${e.employee_id}` : ""),
                      hint: e.department?.name,
                    }))}
                    placeholder="Select employee…"
                    searchPlaceholder="Search by name or ID…"
                    width="100%"
                    disabled={saving}
                  />
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => { setTransferOpen(false); setTransferTargetId(""); }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={submitTransfer}
                    disabled={saving || !transferTargetId}
                  >
                    <Icon name="send" size={12} /> Confirm transfer
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-head"><div className="card-title">Details</div></div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <KV k="Task group" v={tm?.task_group?.name} />
              <KV k="Recurrence" v={tm?.recurrence_pattern || "—"} />
              {/* Priority applies to one-off and recurring tasks alike —
                  recurring instances inherit it from the business ↔ task pivot. */}
              <div>
                <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 500 }}>Priority</div>
                <div style={{ marginTop: 4 }}>
                  <span className={`badge ${PRIORITY_CLS[task.priority] || "badge-neutral"}`}>
                    {PRIORITY_LABEL[task.priority] || "—"}
                  </span>
                </div>
              </div>
              {isAdmin && (
                <>
                  <div className="field">
                    <label className="field-label">Assignee</label>
                    <SearchableSelect
                      value={task.employee_id || ""}
                      onChange={(v) => changeAssignee(v || null)}
                      options={staffOptions}
                      placeholder="— Unassigned —"
                      searchPlaceholder="Search employees…"
                      width="100%"
                      disabled={saving}
                    />
                  </div>
                  <div className="field">
                    <label className="field-label">
                      Reviewer <span className="t-muted text-xs">(optional)</span>
                    </label>
                    <SearchableSelect
                      value={task.reviewer_id || ""}
                      onChange={(v) => changeReviewer(v || null)}
                      options={staffOptions}
                      placeholder="— No reviewer —"
                      searchPlaceholder="Search employees…"
                      width="100%"
                      disabled={saving}
                    />
                    <div className="field-hint">
                      When set, only the reviewer can mark this task complete.
                      Assignee handles the work itself.
                    </div>
                  </div>
                </>
              )}
              {!isAdmin && (
                <>
                  <KV k="Assignee" v={task.employee?.name} />
                  <KV k="Reviewer" v={task.reviewer?.name} />
                </>
              )}
              {isAdmin ? (
                <div className="field">
                  <label className="field-label">Reminder date</label>
                  <input
                    type="date"
                    className="input"
                    value={(task.reminder_date || "").slice(0, 10)}
                    max={(task.due_date || "").slice(0, 10) || undefined}
                    disabled={saving}
                    onChange={(e) => updateDates({ reminder_date: e.target.value || null })}
                  />
                </div>
              ) : (
                <KV k="Reminder date" v={formatDate(task.reminder_date)} />
              )}
              <KV k="Business" v={task.business?.business_name} />
            </div>
          </div>

          {paymentInstance && (
            <PaymentExpensesSection
              instance={paymentInstance}
              canManage={canManageExpenses}
              onChanged={reloadTask}
            />
          )}

          {task.remarks && (
            <div className="card">
              <div className="card-head"><div className="card-title">Remarks</div></div>
              <div className="card-body" style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{task.remarks}</div>
            </div>
          )}

          {Array.isArray(task.assignments) && task.assignments.length > 0 && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Assignment history</div>
                <span className="text-xs t-muted">
                  {task.assignments.length} change{task.assignments.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {task.assignments.map((entry) => (
                  <AssignmentRow key={entry.id} entry={entry} />
                ))}
              </div>
            </div>
          )}

          {/* "Client" used to be a separate parent record; the business
              itself is the client now, so we render that single card. */}
          {task.business && <ClientCard business={task.business} />}

          {Array.isArray(task.business?.field_groups) && task.business.field_groups.length > 0 && (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Business fields</div>
                <span className="text-xs t-muted">
                  {task.business.field_groups.length} group{task.business.field_groups.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {task.business.field_groups.map((g) => (
                  <FieldGroupBlock key={g.id} group={g} />
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-head">
              <div className="card-title">Notes</div>
              <span className="text-xs t-muted">{notes.length} notes</span>
            </div>
            <div className="card-body">
              {notes.length === 0 && (
                <div className="empty-state" style={{ padding: 16 }}>
                  <div className="empty-state-title">No notes yet</div>
                  <div className="text-xs">Add updates, blockers, or observations.</div>
                </div>
              )}
              {notes.map((n) => (
                <div key={n.id} className="note">
                  <div className="note-meta">
                    <span className="note-author">{n.author?.name || "Unknown"}</span>
                    <span>·</span>
                    <span>{formatDate(n.created_at)}</span>
                  </div>
                  <div className="note-body">{n.note}</div>
                </div>
              ))}
              <div style={{ marginTop: 12 }}>
                <textarea
                  className="textarea"
                  placeholder={isAdmin ? "Add admin observation…" : "Add a note (progress, blockers, etc.)"}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                />
                <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                  <button className="btn btn-primary btn-sm" disabled={!reply.trim() || saving} onClick={addNote}>
                    <Icon name="send" size={12} /> Add note
                  </button>
                </div>
              </div>
            </div>
          </div>

          {!isAdmin && isReviewer && !isAssignee && (
            <div
              className="card"
              style={{
                background: "var(--warning-100)",
                borderColor: "#fde68a",
                color: "var(--warning)",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: 12,
              }}
            >
              <Icon name="alert-triangle" size={16} />
              <div className="text-xs" style={{ lineHeight: 1.5 }}>
                You're the <strong>reviewer</strong> on this task — it's currently
                assigned to{" "}
                <strong>{task.employee?.name || "another employee"}</strong>. To
                complete it, the task must be transferred to you (by an admin or the
                assignee) once the work is done.
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function FieldGroupBlock({ group }) {
  const [revealed, setRevealed] = useState({});
  const [justCopied, setJustCopied] = useState(null); // field id, transient
  const fields = Array.isArray(group.fields) ? group.fields : [];

  const copy = async (f) => {
    const value = f.value || "";
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setJustCopied(f.id);
      showToast("success", `${f.field_name} copied`);
      setTimeout(() => setJustCopied((cur) => (cur === f.id ? null : cur)), 1200);
    } catch {
      showToast("error", "Couldn't copy to clipboard");
    }
  };

  if (fields.length === 0) {
    return (
      <div>
        <div className="field-label">{group.name}</div>
        <div className="t-muted text-xs">No fields.</div>
      </div>
    );
  }
  return (
    <div>
      <div className="field-label" style={{ marginBottom: 8 }}>{group.name}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {fields.map((f) => {
          const isPwd = f.field_type === "password";
          const isLink = f.field_type === "link";
          const isRevealed = !!revealed[f.id];
          const copied = justCopied === f.id;
          // Reserve room on the right for the copy and (for passwords) eye buttons.
          const inputRightPad = isPwd ? 68 : 36;
          return (
            <div key={f.id}>
              <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 500 }}>
                {f.field_name}
                {f.is_required && <span style={{ color: "var(--danger)" }}> *</span>}
              </div>
              <div style={{ position: "relative", marginTop: 2 }}>
                {isLink && f.value ? (
                  <a
                    className="input"
                    href={normalizeUrl(f.value)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={f.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      paddingRight: inputRightPad,
                      color: "var(--primary-700)",
                      textDecoration: "none",
                      overflow: "hidden",
                    }}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textDecoration: "underline",
                      }}
                    >
                      {f.value}
                    </span>
                  </a>
                ) : (
                  <input
                    className="input"
                    readOnly
                    value={isPwd ? (isRevealed ? f.value || "" : "••••••••") : f.value || ""}
                    style={{
                      paddingRight: inputRightPad,
                      fontFamily: isPwd && isRevealed ? "var(--font-mono)" : "inherit",
                    }}
                  />
                )}
                <button
                  type="button"
                  className="icon-btn"
                  title={copied ? "Copied" : "Copy"}
                  style={{ position: "absolute", right: isPwd ? 36 : 4, top: 3, width: 28, height: 28, color: copied ? "var(--success)" : undefined }}
                  onClick={() => copy(f)}
                  disabled={!f.value}
                >
                  <Icon name={copied ? "check" : "copy"} size={14} />
                </button>
                {isPwd && (
                  <button
                    type="button"
                    className="icon-btn"
                    title={isRevealed ? "Hide password" : "Show password"}
                    style={{ position: "absolute", right: 4, top: 3, width: 28, height: 28 }}
                    onClick={() => setRevealed((r) => ({ ...r, [f.id]: !isRevealed }))}
                  >
                    <Icon name={isRevealed ? "eye-off" : "eye"} size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AssignmentRow({ entry }) {
  const from = entry.from_employee?.name;
  const to = entry.to_employee?.name;
  const by = entry.changed_by?.name;
  let title;
  if (!from && to) title = `Assigned to ${to}`;
  else if (from && !to) title = `Unassigned from ${from}`;
  else if (from && to) title = `${from} → ${to}`;
  else title = "Reassigned";

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          marginTop: 7,
          background: to ? "var(--primary-500)" : "var(--ink-400)",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="t-primary" style={{ fontSize: 13 }}>{title}</div>
        <div className="t-muted text-xs" style={{ marginTop: 2 }}>
          {formatDateTime(entry.created_at)}
          {by ? ` · by ${by}` : ""}
        </div>
      </div>
    </div>
  );
}

// Treat values like "example.com" as URLs by prepending https://. Real
// schemes (http(s), mailto, tel, ftp) are passed through unchanged.
function normalizeUrl(raw) {
  const v = (raw || "").trim();
  if (!v) return v;
  if (/^(https?:|mailto:|tel:|ftp:)/i.test(v)) return v;
  return "https://" + v;
}

const REGIME_LABEL = { old: "Old regime", new: "New regime" };
const GST_SCHEME_LABEL = { regular: "Regular", composition: "Composition" };
const GST_FREQUENCY_LABEL = { monthly: "Monthly", quarterly: "Quarterly" };

function ClientCard({ business: b }) {
  const [revealed, setRevealed] = useState({});
  const [justCopied, setJustCopied] = useState(null);

  const copy = async (key, value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setJustCopied(key);
      showToast("success", `${label} copied`);
      setTimeout(() => setJustCopied((cur) => (cur === key ? null : cur)), 1200);
    } catch {
      showToast("error", "Couldn't copy to clipboard");
    }
  };

  const fieldProps = (key, label) => ({
    fieldKey: key,
    label,
    copied: justCopied === key,
    onCopy: (value) => copy(key, value, label),
  });
  const secretProps = (key, label) => ({
    ...fieldProps(key, label),
    revealed: !!revealed[key],
    onToggleReveal: () => setRevealed((r) => ({ ...r, [key]: !r[key] })),
  });

  const hasTax = b.pan || b.it_password || b.itr_type || b.regime;
  const hasGst = b.gstin || b.gst_id || b.gst_password || b.gst_frequency || b.gst_scheme;
  const hasEntity = b.entity_type || b.authorised_signatory;
  const hasTan = b.tan_no || b.tan_it_password || b.tan_traces_id || b.tan_traces_password;

  return (
    <div className="card">
      <div className="card-head"><div className="card-title">Client</div></div>
      <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <CopyableField {...fieldProps("client_id", "Client ID")} value={b.client_id} mono />
          <CopyableField {...fieldProps("office_file_no", "Office File No")} value={b.office_file_no} />
          <CopyableField {...fieldProps("client_name", "Legal name")} value={b.client_name} />
          <CopyableField {...fieldProps("business_name", "Trade name")} value={b.business_name} />
          <KV k="Status" v={b.is_active ? "Active" : "Inactive"} />
          <CopyableField {...fieldProps("business_email", "Email")} value={b.business_email} />
          <CopyableField {...fieldProps("business_phone", "Phone")} value={b.business_phone} mono />
          {b.description && (
            <div style={{ gridColumn: "1 / -1" }}>
              <KV k="Description" v={b.description} />
            </div>
          )}
        </div>

        {hasTax && (
          <ClientSubsection title="Income tax">
            <CopyableField {...fieldProps("pan", "PAN")} value={b.pan} mono />
            <CopyableField {...fieldProps("itr_type", "ITR type")} value={b.itr_type} />
            <CopyableField {...fieldProps("regime", "Regime")} value={REGIME_LABEL[b.regime] || b.regime} />
            <CopyableField {...secretProps("it_password", "IT portal password")} value={b.it_password} mono isSecret />
          </ClientSubsection>
        )}

        {hasGst && (
          <ClientSubsection title="GST">
            <CopyableField {...fieldProps("gstin", "GSTIN")} value={b.gstin} mono />
            <CopyableField {...fieldProps("gst_id", "GST portal ID")} value={b.gst_id} />
            <CopyableField {...fieldProps("gst_frequency", "Filing frequency")} value={GST_FREQUENCY_LABEL[b.gst_frequency] || b.gst_frequency} />
            <CopyableField {...fieldProps("gst_scheme", "Scheme")} value={GST_SCHEME_LABEL[b.gst_scheme] || b.gst_scheme} />
            <CopyableField {...secretProps("gst_password", "GST portal password")} value={b.gst_password} mono isSecret />
          </ClientSubsection>
        )}

        {hasEntity && (
          <ClientSubsection title="Entity">
            <CopyableField {...fieldProps("entity_type", "Type of entity")} value={b.entity_type} />
            <CopyableField {...fieldProps("authorised_signatory", "Authorised signatory")} value={b.authorised_signatory} />
          </ClientSubsection>
        )}

        {hasTan && (
          <ClientSubsection title="TAN / TRACES">
            <CopyableField {...fieldProps("tan_no", "TAN no.")} value={b.tan_no} mono />
            <CopyableField {...fieldProps("tan_traces_id", "TRACES ID")} value={b.tan_traces_id} />
            <CopyableField {...secretProps("tan_it_password", "TAN IT password")} value={b.tan_it_password} mono isSecret />
            <CopyableField {...secretProps("tan_traces_password", "TRACES password")} value={b.tan_traces_password} mono isSecret />
          </ClientSubsection>
        )}
      </div>
    </div>
  );
}

function ClientSubsection({ title, children }) {
  return (
    <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12 }}>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

// Read-only input + copy button (+ optional eye toggle for secrets). Mirrors
// the layout used by FieldGroupBlock so the Client card looks consistent
// with the per-business custom fields below it.
function CopyableField({ label, value, mono = false, isSecret = false, revealed = false, copied = false, onCopy, onToggleReveal }) {
  const display = isSecret && !revealed && value ? "••••••••" : (value ?? "");
  const inputRightPad = isSecret ? 68 : 36;
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 500 }}>{label}</div>
      <div style={{ position: "relative", marginTop: 2 }}>
        <input
          className="input"
          readOnly
          value={display}
          style={{
            paddingRight: inputRightPad,
            fontFamily: mono && (!isSecret || revealed) ? "var(--font-mono)" : "inherit",
          }}
        />
        <button
          type="button"
          className="icon-btn"
          title={copied ? "Copied" : "Copy"}
          style={{
            position: "absolute",
            right: isSecret ? 36 : 4,
            top: 3,
            width: 28,
            height: 28,
            color: copied ? "var(--success)" : undefined,
          }}
          onClick={() => onCopy?.(value)}
          disabled={!value}
        >
          <Icon name={copied ? "check" : "copy"} size={14} />
        </button>
        {isSecret && (
          <button
            type="button"
            className="icon-btn"
            title={revealed ? "Hide password" : "Show password"}
            style={{ position: "absolute", right: 4, top: 3, width: 28, height: 28 }}
            onClick={onToggleReveal}
            disabled={!value}
          >
            <Icon name={revealed ? "eye-off" : "eye"} size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 500 }}>{k}</div>
      <div
        style={{
          fontSize: 13,
          fontFamily: mono ? "var(--font-mono)" : "inherit",
          color: "var(--ink-900)",
          marginTop: 1,
          wordBreak: "break-all",
        }}
      >
        {v ?? "—"}
      </div>
    </div>
  );
}
